import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { Card, Grid, Heading, LinkOverlay, Stack } from '@eneco/sparky';

import type { CardGridContent, CardGridReference } from './card-grid-content.types';
import { gridColumns } from './gridColumns';

/**
 * `cardGrid` block for the CMS-agnostic lane. POC-schema-only (see `card-grid-content.types.ts`).
 * `references` arrive pre-resolved by the GROQ projection — no `adapter.toLink`/reference stub
 * involved. Href is a bare `/<slug>` (no `_type`-based path prefix), the same simplification
 * `mappers.ts#toLink`'s own NOTE comment flags as needing routing-team input eventually — kept
 * consistent rather than inventing a different convention here.
 */
const CardGrid: FC = () => {
  const content = useContent<CardGridContent>();
  const references = content.references ?? [];

  if (references.length === 0) return null;

  return (
    <Stack gap="6">
      {content.title && <Heading as="h2">{content.title}</Heading>}
      <Grid columns={{ initial: 1, md: gridColumns(references.length) }} gap="6">
        {references.map(reference => (
          <ReferenceCard key={reference._id} reference={reference} />
        ))}
      </Grid>
    </Stack>
  );
};

const ReferenceCard: FC<{ reference: CardGridReference }> = ({ reference }) => {
  if (!reference.slug) return null;

  return (
    <LinkOverlay>
      <Card>
        <Card.Content>
          <LinkOverlay.Link href={`/${reference.slug}`}>
            <Heading as="h3" size="XS">
              {reference.title ?? reference.slug}
            </Heading>
          </LinkOverlay.Link>
        </Card.Content>
      </Card>
    </LinkOverlay>
  );
};

export default CardGrid;
